import { Box, Button, IconButton, Typography, useTheme, alpha } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useCallback, ReactNode } from "react";
import { set } from "../../../../utils";

interface FormFieldArrayProps<T> {
    title: string;
    items: T[];
    fieldPath: string;
    handleFieldChange: (fieldPath: string, value: any) => void;
    emptyItem: T;
    renderItem: (
        item: T,
        itemIndex: number,
        handleChange: (idx: number, field: keyof T | string, value: any) => void,
    ) => ReactNode;
    addButtonLabel?: string;
    emptyMessage?: string;
    columns?: number;
    itemLabelTemplate?: string;
    importantColor?: string;
    itemGap?: number;
}

const FormFieldArray = <T extends Record<string, any>>({
    title,
    items = [],
    fieldPath,
    handleFieldChange,
    emptyItem,
    renderItem,
    addButtonLabel = "Add Item",
    emptyMessage,
    columns,
    itemLabelTemplate,
    importantColor,
    itemGap,
}: FormFieldArrayProps<T>) => {
    const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const theme = useTheme();

    const handleAdd = useCallback(() => {
        const newItems = [...items, emptyItem];
        handleFieldChange(fieldPath, newItems);
    }, [items, fieldPath, handleFieldChange, emptyItem]);

    const handleRemove = useCallback(
        (index: number) => {
            const newItems = items.filter((_, i) => i !== index);
            handleFieldChange(fieldPath, newItems);
        },
        [items, fieldPath, handleFieldChange],
    );

    const handleItemChange = useCallback(
        (itemIndex: number, field: keyof T | string, value: any) => {
            const newItems = [...items];
            if (!newItems[itemIndex]) {
                newItems[itemIndex] = { ...emptyItem };
            }
            // Use set utility to handle nested paths like "columnD.percentOfStock"
            newItems[itemIndex] = set(newItems[itemIndex], String(field), value);
            handleFieldChange(fieldPath, newItems);
        },
        [items, fieldPath, handleFieldChange, emptyItem],
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {title && (
                    <Typography
                        variant="subtitle2"
                        sx={{ whiteSpace: "nowrap", ...(importantColor && { color: importantColor, fontWeight: 600 }) }}
                    >
                        {title}
                    </Typography>
                )}
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    size="medium"
                    variant="contained"
                    fullWidth
                    sx={importantColor ? { backgroundColor: importantColor, "&:hover": { backgroundColor: importantColor, filter: "brightness(0.9)" } } : undefined}
                >
                    {addButtonLabel}
                </Button>
            </Box>

            {/* Always render at least one item. When the underlying array is empty, show a
                placeholder row derived from emptyItem — editing any field promotes it into a
                real item via handleItemChange. */}
            {(items.length === 0 ? [emptyItem] : items).map((item, index) => {
                const isPlaceholder = items.length === 0;
                return (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            my: 2,
                            ml: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.action.disabled, 0.08),
                            alignItems: "flex-start",
                        }}
                    >
                        {itemLabelTemplate && (
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    pl: 1,
                                    whiteSpace: "nowrap",
                                    alignSelf: "center",
                                }}
                            >
                                {itemLabelTemplate.replace("{alpha}", LETTERS[index] || String(index + 1))}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                p: 1.5,
                                pl: 1,
                                flex: 1,
                                display: "grid",
                                gridTemplateColumns: columns
                                    ? `repeat(${columns}, 1fr)`
                                    : "repeat(auto-fill, minmax(150px, 1fr))",
                                gap: itemGap ?? 2,
                                alignItems: "center",
                            }}
                        >
                            {renderItem(item, index, handleItemChange)}
                        </Box>
                        <IconButton
                            onClick={() => handleRemove(index)}
                            color="error"
                            size="small"
                            sx={{ alignSelf: "center", visibility: isPlaceholder ? "hidden" : "visible" }}
                            disabled={isPlaceholder}
                            aria-hidden={isPlaceholder}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                );
            })}
            {items.length === 0 && emptyMessage && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", ml: 2, mb: 1, fontStyle: "italic" }}
                >
                    {emptyMessage}
                </Typography>
            )}
        </Box>
    );
};

export default FormFieldArray;
